import { useCallback, useRef, useSyncExternalStore } from 'react';
import type { StoredValue } from './storage';

/**
 * A stored value as React state — kept in step with the OTHER TABS as well.
 *
 * Before this, every stored setting was read once into `useState`, so the app
 * open in a second window went on showing the old language until somebody
 * reloaded it. Two windows are normal here: the Auswertung beside the
 * Befundtabelle, the Zeiterfassung beside the Tagebuch.
 *
 * The snapshot is compared by its JSON, not by identity: `read()` builds a new
 * object every time (deliberately — see `fallback` in storage.ts), and
 * `useSyncExternalStore` would take a fresh object for a fresh value and
 * re-render for ever.
 */
export function useStoredValue<T>(value: StoredValue<T>): T {
    const cache = useRef<{ json: string; value: T } | null>(null);

    const subscribe = useCallback(
        (onStoreChange: () => void) => value.subscribe(onStoreChange),
        [value]
    );

    const getSnapshot = useCallback(() => {
        const next = value.read();
        const json = JSON.stringify(next);
        if (!cache.current || cache.current.json !== json) {
            cache.current = { json, value: next };
        }
        return cache.current.value;
    }, [value]);

    // The same snapshot serves the server render: there is none here (this is a
    // pure SPA), and a stored value is the same question either way.
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
